/**
 * Autoplay video — defensive playback, deferred for below-fold media.
 *
 * Two paths, split by selector:
 *
 * `.hero__video` (above the fold) ships `autoplay preload="metadata"` rather
 * than preload="auto", so a 2MB+ background video isn't buffered in full ahead
 * of the rest of the page on a mobile connection. Measured as neutral for LCP
 * — it did not turn out to be what held mobile LCP at ~8s — but it keeps the
 * video from monopolising bandwidth it doesn't need yet. autoplay still fetches
 * enough to start, and the poster paints immediately either way. Some browsers
 * block autoplay until the document is ready or inside an iframe (Shopify theme
 * editor preview), so .muted is forced (some Chrome configs strip it) and
 * play() is re-attempted on loadeddata / canplay.
 *
 * `[data-autoplay-video]` (below the fold) ships WITHOUT the autoplay attribute
 * and with preload="none", so nothing downloads on page load — the homepage
 * otherwise fetched 18 videos at once, starving the hero image of bandwidth.
 * play() is called as each one approaches the viewport, which is what starts
 * the fetch, so the autoplay behaviour a visitor sees is unchanged. rootMargin
 * gives it a head start so the poster is normally replaced before the video is
 * scrolled into view; it applies on all four sides, which also covers the
 * horizontally scrolled cocktail rail.
 *
 * Errors are swallowed — we never break the page if the browser refuses
 * (the poster still shows).
 */
(function () {
  if (typeof document === 'undefined') return;

  var ROOT_MARGIN = '600px';

  var play = function (video) {
    if (!video) return;
    video.muted = true;
    var promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {});
    }
  };

  var claim = function (video) {
    if (video.dataset.heroAutoplayBound === '1') return false;
    video.dataset.heroAutoplayBound = '1';
    return true;
  };

  var initEager = function (video) {
    if (!claim(video)) return;

    if (video.readyState >= 2) {
      play(video);
    } else {
      video.addEventListener('loadeddata', function () { play(video); }, { once: true });
      video.addEventListener('canplay', function () { play(video); }, { once: true });
    }
  };

  var observer = null;
  if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          play(entry.target);
        });
      },
      { rootMargin: ROOT_MARGIN }
    );
  }

  var initLazy = function (video) {
    if (!claim(video)) return;

    if (observer) {
      observer.observe(video);
    } else {
      // No IntersectionObserver: fall back to playing immediately, which also
      // triggers the fetch that preload="none" skipped.
      play(video);
    }
  };

  var initAll = function () {
    // Eager first: a video carrying both hooks should keep hero behaviour.
    document.querySelectorAll('.hero__video').forEach(initEager);
    document.querySelectorAll('[data-autoplay-video]').forEach(initLazy);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // Re-run when sections re-render in the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();
